import { useMutation } from "@apollo/client";
import { CREATE_CONVERSATION_MUTATION } from "../graphql/mutations";
import { LIST_CONVERSATIONS_QUERY } from "../graphql/queries";
import type {
  CreateConversationMutation,
  CreateConversationMutationVariables,
  ListConversationsQuery,
} from "../generated/graphql";

interface UseCreateConversationReturn {
  createConversation: (options: {
    title?: string;
    assistantId: string;
  }) => Promise<CreateConversationMutation["createConversation"]["conversation"]>;
  loading: boolean;
  error: Error | undefined;
}

export const useCreateConversation = (): UseCreateConversationReturn => {
  const [mutate, { loading, error }] = useMutation<
    CreateConversationMutation,
    CreateConversationMutationVariables
  >(CREATE_CONVERSATION_MUTATION, {
    update(cache, { data }) {
      if (!data?.createConversation?.conversation) return;

      // Update the conversations list cache
      try {
        const existingData = cache.readQuery<ListConversationsQuery>({
          query: LIST_CONVERSATIONS_QUERY,
          variables: {
            limit: 50,
            includeArchived: false,
          },
        });

        if (existingData?.listConversations) {
          cache.writeQuery<ListConversationsQuery>({
            query: LIST_CONVERSATIONS_QUERY,
            variables: {
              limit: 50,
              includeArchived: false,
            },
            data: {
              listConversations: {
                ...existingData.listConversations,
                conversations: [
                  data.createConversation.conversation,
                  ...(existingData.listConversations.conversations || []),
                ],
                totalCount: (existingData.listConversations.totalCount || 0) + 1,
              },
            },
          });
        }
      } catch (e) {
        // Cache might not have this query yet, that's okay
        console.log("Cache update skipped:", e);
      }
    },
    onError: error => {
      console.error("Error creating conversation:", error);
    },
  });

  const createConversation = async ({
    title,
    assistantId,
  }: {
    title?: string;
    assistantId: string;
  }) => {
    const result = await mutate({
      variables: {
        input: {
          title,
          assistantId,
        },
      },
    });

    if (!result.data?.createConversation?.conversation) {
      throw new Error("Failed to create conversation");
    }

    return result.data.createConversation.conversation;
  };

  return {
    createConversation,
    loading,
    error,
  };
};